exports.createConsentTracking = (win, config) => {
  const doc = win.document;
  let statistics = false;
  let marketing = false;
  let stopping = false;
  let matomoStarted = false;
  let matomoReady = false;
  let metaStarted = false;
  let routeReady = false;
  let matomoPage;
  let metaPage;
  let routeTimer;

  const consent = (category) =>
    !stopping && win.Cookiebot?.consent?.[category] === true;

  function loadScript(src, onload) {
    const script = doc.createElement("script");
    script.async = true;
    script.src = src;
    if (onload) script.addEventListener("load", onload, { once: true });
    doc.head.appendChild(script);
  }

  function trackPage() {
    if (!routeReady || stopping) return;
    const url = win.location.href;
    if (matomoReady && consent("statistics") && matomoPage !== url) {
      matomoPage = url;
      // The published container owns URL/title sanitization and page views.
      win._mtm.push({ event: "gatsby-route-change" });
    }
    if (metaStarted && consent("marketing") && metaPage !== url) {
      metaPage = url;
      win.fbq("track", "PageView");
    }
  }

  function startMatomo() {
    if (matomoStarted || !config.matomoContainerUrl) return;
    matomoStarted = true;
    win._paq = win._paq || [];
    win._mtm = win._mtm || [];
    win._paq.push(["requireConsent"]);
    win._mtm.push({ "mtm.startTime": Date.now(), event: "mtmStart" });
    loadScript(config.matomoContainerUrl, () => {
      const ready = () => {
        // The container initializes on DOM ready and can miss the original
        // Cookiebot event while downloading. Grant consent after its setup.
        win.setTimeout(() => {
          if (!consent("statistics")) return;
          win._paq.push(["setConsentGiven"]);
          matomoReady = true;
          trackPage();
        }, 0);
      };
      if (doc.readyState === "loading") {
        doc.addEventListener("DOMContentLoaded", ready, { once: true });
      } else {
        ready();
      }
    });
  }

  function startMeta() {
    if (metaStarted || !config.metaPixelId) return;
    metaStarted = true;
    const fbq = function () {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    win.fbq = win._fbq = fbq;
    fbq("consent", "grant");
    fbq("init", config.metaPixelId);
    loadScript("https://connect.facebook.net/en_US/fbevents.js");
  }

  function clearCookies(pattern) {
    const domains = ["", win.location.hostname, ".lararnasakassa.se"];
    for (const cookie of doc.cookie.split(";")) {
      const name = cookie.split("=")[0].trim();
      if (!pattern.test(name)) continue;
      for (const domain of domains) {
        doc.cookie = `${name}=; Max-Age=0; Path=/;${
          domain ? ` Domain=${domain};` : ""
        }`;
      }
    }
  }

  function clearStorage(storage, keys) {
    try {
      for (const key of keys) win[storage].removeItem(key);
    } catch {
      // Storage can be disabled independently of cookie consent.
    }
  }

  function syncConsent() {
    if (stopping) return;
    const nextStatistics = consent("statistics");
    const nextMarketing = consent("marketing");
    const revoked =
      (statistics && !nextStatistics) || (marketing && !nextMarketing);
    statistics = nextStatistics;
    marketing = nextMarketing;

    if (revoked) {
      stopping = true;
      win.clearTimeout(routeTimer);
      // Stop both trackers during the transition. Reloading removes container
      // timers/listeners too; removing script elements cannot undo execution.
      if (win._paq) {
        win._paq.push(["forgetConsentGiven"]);
      }
      if (win.fbq) {
        if (!win.fbq.callMethod) win.fbq.queue = [];
        win.fbq("consent", "revoke");
      }
    }

    // Also remove identifiers left by the previous, ungated implementation.
    if (!statistics) {
      clearCookies(/^_pk_/);
      clearStorage("sessionStorage", ["mtmReferrer", "lastLinkClick"]);
    }
    if (!marketing) {
      clearCookies(/^_fb[pc]$/);
      clearStorage("localStorage", [
        "lastExternalReferrer",
        "lastExternalReferrerTime",
      ]);
    }
    if (revoked) {
      win.location.reload();
      return;
    }
    if (statistics) startMatomo();
    if (marketing) startMeta();
    trackPage();
  }

  for (const event of [
    "CookiebotOnConsentReady",
    "CookiebotOnAccept",
    "CookiebotOnDecline",
  ]) {
    win.addEventListener(event, syncConsent);
  }
  // Cookiebot can finish before Gatsby hydrates, including on return visits.
  if (win.Cookiebot?.hasResponse) syncConsent();

  return {
    onRouteUpdate() {
      routeReady = false;
      win.clearTimeout(routeTimer);
      // Match Gatsby's title update timing, coalescing superseded navigations.
      routeTimer = win.setTimeout(() => {
        routeReady = true;
        syncConsent();
      }, 50);
    },
  };
};
